/**
 * @genesis_version V9.1
 * @genesis_hub The Theme Cascade Activator
 * @description
 * Single-point declarative hook that toggles the `data-theme` HTML attribute for CSS-driven
 * theme switching. Resolves the user's selected theme name to a CSS slug via THEME_SLUG_MAP
 * and applies/removes the attribute — triggering the entire CSS cascade without JavaScript
 * setProperty() loops or JSONB token payloads.
 * @blast_radius
 * - Downstream: Every component consuming `var(--color-*)` tokens, every `.theme-*` utility
 *   class, the entire visual rendering pipeline. All ~179 components depend on this hook.
 * - Upstream: usePreferencesViewModel (provides the active theme name), THEME_SLUG_MAP
 *   (static name→slug resolution table).
 * @invariants
 * - MUST use declarative `data-theme` attribute toggling on <html> (setProperty() is a
 *   BLOCKING VIOLATION per Rule 16, Part 2.4).
 * - MUST NOT store token values in JavaScript — CSS files are the single source of truth
 *   for all token values (Rule 16, Part 3).
 * - Cleanup MUST remove the data-theme attribute on unmount to prevent stale state.
 */

import { useEffect, useRef } from 'react';
import { usePreferencesViewModel } from './usePreferencesViewModel.js';

const THEME_SLUG_MAP: Record<string, string> = {
    'Default Glass': 'default-glass',
    'Matcha Horizon': 'matcha-horizon',
    'Midnight Obsidian': 'midnight-obsidian',
    'Midnight Eagle': 'midnight-eagle',
};

/** Maps CSS slug → browser toolbar color for dynamic <meta name="theme-color"> sync */
const THEME_COLOR_MAP: Record<string, string> = {
    'default-glass': '#F8FAFC',
    'matcha-horizon': '#ECFDF5',
    'midnight-obsidian': '#020617',
    'midnight-eagle': '#020617',
};

export function useThemeInjector() {
    const { globalTheme } = usePreferencesViewModel();
    const previousSlugRef = useRef<string | null>(null);

    useEffect(() => {
        const slug = globalTheme ? (THEME_SLUG_MAP[globalTheme] ?? null) : null;

        if (slug) {
            document.documentElement.setAttribute('data-theme', slug);
            previousSlugRef.current = slug;

            // Dynamically sync <meta name="theme-color"> to active data-theme
            const meta = document.querySelector('meta[name="theme-color"]');
            if (meta) {
                const color = THEME_COLOR_MAP[slug];
                if (color) meta.setAttribute('content', color);
            }
        } else {
            document.documentElement.removeAttribute('data-theme');
            previousSlugRef.current = null;
        }

        return () => {
            document.documentElement.removeAttribute('data-theme');
            previousSlugRef.current = null;
        };
    }, [globalTheme]);
}
